/* Valor purchase history — "last ordered" notes on product cards (v2).
 *
 * Builds a per-dealer SKU -> {qty, ts} index of the last 8 months of orders by
 * trickling the valor proxy's order-list / order-detail actions (same auth and
 * contract as assets/valor-account.js) into IndexedDB, then appends a passive
 * reorder note ("24 units · Mar 2026") to the right side of every
 * .vpc-stock[data-vpc-sku] card stock line, opposite the inventory status
 * (below the Restock ETA when one is present — CSS in the snippet). Replaces
 * the v1 Liquid customer.orders dump, which was capped at ~50 recent orders.
 *
 * Boot modes (config from snippets/valor-purchase-history.liquid):
 *   guest    — no config: delete any stale DB left by an interrupted logout.
 *   non-B2B  — no proxy auth: reduce the legacy window.VALOR_ORDER_LINES dump
 *              in memory (v1 behavior, ~50-order ceiling) and render.
 *   B2B      — full engine: hydrate notes from IndexedDB immediately, then
 *              resume/continue the background sync (state machine persisted in
 *              the DB, so any page unload resumes at order granularity).
 *
 * Load control: one proxy request per second wrapped in requestIdleCallback,
 * paused while the tab is hidden, one syncing tab at a time via Web Locks,
 * 10s/30s/90s backoff on failures with a hard stop after 5 in a row.
 * Storage is purged on logout (shadow-DOM-aware click hook + next-load sweep)
 * and whenever the stored customer id doesn't match the current one.
 */
(function () {
  'use strict';
  if (window.__vphEngine) return;
  window.__vphEngine = true;

  var DB_NAME = 'valor-vph';
  var DB_VERSION = 1;
  var OWNER_KEY = 'vph:owner';
  var TICK_MS = 1000;
  var IDLE_TIMEOUT_MS = 2000;
  var BACKOFF_MS = [10000, 30000, 90000];
  var MAX_CONSECUTIVE_FAILURES = 5;
  var MAX_ITEM_ATTEMPTS = 3;
  var MAX_LIST_PAGES = 40;
  var TOPUP_PAGE_CAP = 5;
  /* ERP invoices carry date-only createdAt (midnight UTC), so a same-day
     invoice can predate the build watermark by up to ~24h + timezone. A 48h
     re-check window keeps them from being missed; reprocessing inside the
     window is idempotent (merge rule replaces same-order entries). */
  var TOPUP_SLOP_MS = 48 * 3600 * 1000;
  var MONTH_MS = 30.44 * 86400000;
  var LOCK_RETRY_MS = 60000;
  var META_SKU_RE = /SKU:\s*(\S+)/;

  var config = window.__vphConfig || null;
  var months = (config && config.months) || 8;

  /* ── storage helpers (all best-effort) ─────────────────────────────── */

  function lsGet(k) { try { return localStorage.getItem(k); } catch (_) { return null; } }
  function lsSet(k, v) { try { localStorage.setItem(k, v); } catch (_) {} }
  function lsRemove(k) { try { localStorage.removeItem(k); } catch (_) {} }
  function ssGet(k) { try { return sessionStorage.getItem(k); } catch (_) { return null; } }
  function ssSet(k, v) { try { sessionStorage.setItem(k, v); } catch (_) {} }
  function ssRemove(k) { try { sessionStorage.removeItem(k); } catch (_) {} }

  function topupKey() { return 'vph_topup_' + (config ? config.customerId : ''); }

  /* ── minimal promisified IndexedDB wrapper ─────────────────────────── */

  var dbPromise = null;
  var dbConnection = null;

  function openDb() {
    if (dbPromise) return dbPromise;
    dbPromise = new Promise(function (resolve, reject) {
      var req;
      try { req = indexedDB.open(DB_NAME, DB_VERSION); } catch (e) { reject(e); return; }
      req.onupgradeneeded = function () {
        var db = req.result;
        if (!db.objectStoreNames.contains('skus')) {
          db.createObjectStore('skus', { keyPath: 'sku' }).createIndex('ts', 'ts');
        }
        if (!db.objectStoreNames.contains('queue')) {
          db.createObjectStore('queue', { keyPath: 'id' }).createIndex('ts', 'ts');
        }
        if (!db.objectStoreNames.contains('meta')) {
          db.createObjectStore('meta', { keyPath: 'key' });
        }
      };
      req.onsuccess = function () { dbConnection = req.result; resolve(req.result); };
      req.onerror = function () { reject(req.error); };
    });
    return dbPromise;
  }

  function idbRequest(storeName, mode, run) {
    return openDb().then(function (db) {
      return new Promise(function (resolve, reject) {
        var req = run(db.transaction(storeName, mode).objectStore(storeName));
        req.onsuccess = function () { resolve(req.result); };
        req.onerror = function () { reject(req.error); };
      });
    });
  }

  function idbGet(store, key) { return idbRequest(store, 'readonly', function (s) { return s.get(key); }); }
  function idbGetAll(store) { return idbRequest(store, 'readonly', function (s) { return s.getAll(); }); }
  function idbPut(store, value) { return idbRequest(store, 'readwrite', function (s) { return s.put(value); }); }
  function idbDelete(store, key) { return idbRequest(store, 'readwrite', function (s) { return s.delete(key); }); }
  function idbClear(store) { return idbRequest(store, 'readwrite', function (s) { return s.clear(); }); }

  function idbPutAll(storeName, values) {
    if (!values.length) return Promise.resolve();
    return openDb().then(function (db) {
      return new Promise(function (resolve, reject) {
        var t = db.transaction(storeName, 'readwrite');
        var s = t.objectStore(storeName);
        values.forEach(function (v) { s.put(v); });
        t.oncomplete = function () { resolve(); };
        t.onerror = function () { reject(t.error); };
        t.onabort = function () { reject(t.error); };
      });
    });
  }

  /* Newest order still waiting in the queue (ts index, descending). */
  function idbNewestQueueItem() {
    return openDb().then(function (db) {
      return new Promise(function (resolve, reject) {
        var req = db.transaction('queue', 'readonly').objectStore('queue').index('ts').openCursor(null, 'prev');
        req.onsuccess = function () { resolve(req.result ? req.result.value : null); };
        req.onerror = function () { reject(req.error); };
      });
    });
  }

  function deleteDatabase(onDone) {
    try { if (dbConnection) dbConnection.close(); } catch (_) {}
    dbConnection = null;
    dbPromise = null;
    try {
      var req = indexedDB.deleteDatabase(DB_NAME);
      req.onsuccess = function () { if (onDone) onDone(); };
    } catch (_) {}
  }

  /* ── renderer (ported from v1 snippets/valor-purchase-history.liquid) ── */

  var RECEIPT_SVG =
    '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
    'stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
    '<path d="M5 21V5a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v16l-3-2-2 2-2-2-2 2-2-2-3 2z"/>' +
    '<path d="M9 8h6M9 12h6"/></svg>';

  var lastOrdered = new Map(); // sku -> {ts(ms), qty, date?} (date only on the legacy path)
  var rendererStarted = false;

  function unitLabel(n) { return n + (n === 1 ? ' unit' : ' units'); }

  function fmtMonthYear(ts) {
    return new Date(ts).toLocaleString('en-US', { month: 'short', year: 'numeric' });
  }

  function setNoteText(note, entry) {
    if (!note.querySelector('.reorder-note__txt')) {
      note.innerHTML = RECEIPT_SVG + '<span class="reorder-note__txt"></span>';
    }
    note.querySelector('.reorder-note__txt').textContent =
      unitLabel(entry.qty) + ' · ' + (entry.date || fmtMonthYear(entry.ts));
  }

  function applyNote(stockEl) {
    var sku = stockEl.getAttribute('data-vpc-sku');
    var entry = sku ? lastOrdered.get(sku) : null;
    if (!entry || entry.ts < Date.now() - months * MONTH_MS) return; // never/too-long-ago ordered: no note

    var sig = entry.ts + '-' + entry.qty;
    var note = stockEl.querySelector('.reorder-note');
    if (note && note.getAttribute('data-vph-sig') === sig) return;
    if (!note) {
      note = document.createElement('span');
      note.className = 'reorder-note';
      note.setAttribute('data-last-ordered', '');
      note.setAttribute('title', 'Your most recent order of this item');
      stockEl.appendChild(note);
    }
    note.setAttribute('data-vph-sig', sig);
    setNoteText(note, entry);
  }

  function scan(root) {
    if (!lastOrdered.size) return;
    (root || document).querySelectorAll('.vpc-stock[data-vpc-sku]').forEach(applyNote);
  }

  function startRenderer() {
    if (rendererStarted) return;
    rendererStarted = true;

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () { scan(); });
    } else {
      scan();
    }

    document.addEventListener('vph:updated', function () { scan(); });

    // Re-decorate cards injected by collection filtering / pagination / morph.
    var pending = false;
    var observer = new MutationObserver(function (mutations) {
      if (pending) return;
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].addedNodes && mutations[i].addedNodes.length) {
          pending = true;
          window.requestAnimationFrame(function () {
            pending = false;
            scan();
          });
          break;
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function notifyUpdated(skus) {
    try {
      document.dispatchEvent(new CustomEvent('vph:updated', { detail: { skus: skus || [] } }));
    } catch (_) { scan(); }
  }

  /* ── proxy client (contract shared with assets/valor-account.js) ────── */

  function proxyPost(body) {
    var auth = window.__vaAuth || {};
    var payload = {
      customer_id: auth.customer_id,
      ts: auth.ts,
      token: auth.token,
      shop: (window.Shopify && window.Shopify.shop) || (config && config.shop) || ''
    };
    for (var k in body) { if (Object.prototype.hasOwnProperty.call(body, k)) payload[k] = body[k]; }
    return fetch(auth.proxy || window.__vaProxy || '', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (r) {
      if (!r.ok) {
        var httpErr = new Error('HTTP ' + r.status);
        if (r.status === 401 || r.status === 403) httpErr.vphAuth = true;
        throw httpErr;
      }
      return r.json();
    }).then(function (d) {
      if (!d || !d.ok) {
        var err = new Error((d && d.error) || 'Proxy error');
        if (d && /auth|token|signature|hmac|expired|denied|forbidden/i.test(String(d.error || ''))) err.vphAuth = true;
        throw err;
      }
      return d;
    });
  }

  /* ── sync engine ───────────────────────────────────────────────────── */

  var syncState = null;      // in-memory copy of the meta 'state' record
  var stopped = false;       // hard stop for this page load
  var running = false;       // a loop (lock held) is active
  var loopResolve = null;    // resolving this releases the Web Lock
  var tickTimer = null;
  var lockRetryTimer = null;
  var consecutiveFailures = 0;
  var consecutiveDrops = 0;  // items dropped back-to-back — 2 in a row means systemic outage
  var itemAttempts = {};     // order id -> failed attempts this page load
  var topup = null;          // in-memory top-up cursor {page, newest, queued}

  function freshState() {
    var now = Date.now();
    return {
      key: 'state',
      customerId: String(config.customerId),
      phase: 'enumerating',
      page: 1,
      cutoffTs: now - months * MONTH_MS,
      candidateWatermarkTs: now,
      watermarkTs: 0,
      enumeratedCount: 0,
      processedCount: 0,
      skippedCount: 0,
      lastSyncAt: 0,
      lastTopUpAt: 0,
      createdAt: now
    };
  }

  function saveState() { return idbPut('meta', syncState); }

  function syncNeeded() {
    if (!syncState) return false;
    if (syncState.phase !== 'complete') return true;
    return !ssGet(topupKey());
  }

  /* Another tab may have advanced the sync while we waited for the lock —
     re-read the persisted state before running. */
  function reloadState() {
    return idbGet('meta', 'state').then(function (state) {
      if (state && String(state.customerId) === String(config.customerId)) syncState = state;
    }).catch(function () {});
  }

  function maybeStartSync() {
    if (stopped || running || document.hidden || !syncNeeded()) return;
    if (navigator.locks && navigator.locks.request) {
      running = true;
      navigator.locks.request('vph-sync', { ifAvailable: true }, function (lock) {
        if (!lock) { running = false; scheduleLockRetry(); return; }
        return reloadState().then(runLoop); // lock held until this promise settles
      }).catch(function () { running = false; });
    } else {
      running = true;
      runLoop();
    }
  }

  function scheduleLockRetry() {
    if (lockRetryTimer) return;
    lockRetryTimer = setTimeout(function () {
      lockRetryTimer = null;
      maybeStartSync();
    }, LOCK_RETRY_MS);
  }

  function runLoop() {
    return new Promise(function (resolve) {
      loopResolve = resolve;
      tickSoon(0);
    });
  }

  function endLoop() {
    clearTimeout(tickTimer);
    tickTimer = null;
    running = false;
    if (loopResolve) { loopResolve(); loopResolve = null; }
  }

  function tickSoon(delayMs) {
    clearTimeout(tickTimer);
    tickTimer = setTimeout(function () {
      if (window.requestIdleCallback) {
        window.requestIdleCallback(tick, { timeout: IDLE_TIMEOUT_MS });
      } else {
        tick();
      }
    }, delayMs);
  }

  function tick() {
    if (stopped || document.hidden) { endLoop(); return; }
    step().then(function (hasMoreWork) {
      consecutiveFailures = 0;
      if (hasMoreWork) tickSoon(TICK_MS);
      else endLoop();
    }, function (err) {
      consecutiveFailures++;
      if (err && err.vphAuth) { stopped = true; endLoop(); return; } // fresh token needs a navigation
      if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) { stopped = true; endLoop(); return; }
      tickSoon(BACKOFF_MS[Math.min(consecutiveFailures - 1, BACKOFF_MS.length - 1)]);
    });
  }

  function step() {
    if (!syncState) return Promise.resolve(false);
    if (syncState.phase === 'enumerating') return stepEnumerate();
    if (syncState.phase === 'processing') return stepProcess();
    if (syncState.phase === 'complete') return stepTopUp();
    return Promise.resolve(false);
  }

  /* Phase 1: page through order-list, queueing orders inside the 8-month
     window. Assumes newest-first but stops defensively when a whole page
     falls outside the window. */
  function stepEnumerate() {
    return proxyPost({ action: 'order-list', page: syncState.page }).then(function (data) {
      var orders = data.orders || [];
      var anyInWindow = false;
      var rows = [];
      orders.forEach(function (o) {
        var ts = Date.parse(o.createdAt || '');
        if (!isFinite(ts)) return;
        if (syncState.page === 1 && ts > syncState.candidateWatermarkTs) syncState.candidateWatermarkTs = ts;
        if (ts < syncState.cutoffTs) return;
        anyInWindow = true;
        if (o.lineItemsCount === 0 || o.cancelledAt) return;
        rows.push({ id: String(o.id), source: o._source || 'order', sellTo: o._sellToCustomerNo || '', ts: ts });
      });
      return idbPutAll('queue', rows).then(function () {
        syncState.enumeratedCount += rows.length;
        if (!data.hasMore || !anyInWindow || syncState.page >= MAX_LIST_PAGES) {
          syncState.phase = 'processing';
        } else {
          syncState.page += 1;
        }
        return saveState().then(function () { return true; });
      });
    });
  }

  /* Phase 2: newest-first, one order-detail per tick, merged into 'skus'. */
  function stepProcess() {
    return idbNewestQueueItem().then(function (item) {
      if (!item) return finishBuild().then(function () { return false; });
      return proxyPost({
        action: 'order-detail',
        order_id: item.id,
        source: item.source || '',
        sell_to_customer_no: item.sellTo || ''
      }).then(function (data) {
        var order = data.data || {};
        var orderTs = Date.parse(order.createdAt || '');
        if (!isFinite(orderTs)) orderTs = item.ts;
        var bySku = aggregateLines(order.lineItems && order.lineItems.nodes);
        return mergeOrderLines(item.id, orderTs, bySku);
      }).then(function (changed) {
        consecutiveDrops = 0;
        changed.forEach(function (e) { lastOrdered.set(e.sku, { ts: e.ts, qty: e.qty }); });
        return idbDelete('queue', item.id).then(function () {
          syncState.processedCount += 1;
          return saveState();
        }).then(function () {
          if (changed.length) notifyUpdated(changed.map(function (e) { return e.sku; }));
          return true;
        });
      }).catch(function (err) {
        if (err && err.vphAuth) throw err;
        itemAttempts[item.id] = (itemAttempts[item.id] || 0) + 1;
        if (itemAttempts[item.id] < MAX_ITEM_ATTEMPTS) throw err; // backoff, retry same item
        // A poison order (persistently failing while others succeed) is
        // dropped so the build never stalls; a second drop in a row means the
        // proxy itself is down — keep the row and let the hard stop preserve
        // the queue for the next page load.
        consecutiveDrops += 1;
        if (consecutiveDrops >= 2) throw err;
        return idbDelete('queue', item.id).then(function () {
          syncState.skippedCount += 1;
          return saveState();
        }).then(function () { return true; });
      });
    });
  }

  /* Per-session incremental top-up once a build is complete: re-check
     order-list page 1 for orders newer than the watermark (minus the 48h
     invoice-date slop), queue them, and fall back into 'processing'. */
  function stepTopUp() {
    if (ssGet(topupKey())) return Promise.resolve(false);
    if (!topup) topup = { page: 1, newest: syncState.watermarkTs || 0, queued: 0 };
    return proxyPost({ action: 'order-list', page: topup.page }).then(function (data) {
      var orders = data.orders || [];
      var threshold = (syncState.watermarkTs || 0) - TOPUP_SLOP_MS;
      var allNewer = orders.length > 0;
      var rows = [];
      orders.forEach(function (o) {
        var ts = Date.parse(o.createdAt || '');
        if (!isFinite(ts)) return;
        if (ts > topup.newest) topup.newest = ts;
        if (ts <= threshold) { allNewer = false; return; }
        if (o.lineItemsCount === 0 || o.cancelledAt) return;
        rows.push({ id: String(o.id), source: o._source || 'order', sellTo: o._sellToCustomerNo || '', ts: ts });
      });
      return idbPutAll('queue', rows).then(function () {
        topup.queued += rows.length;
        if (allNewer && data.hasMore && topup.page < TOPUP_PAGE_CAP) {
          topup.page += 1;
          return true;
        }
        ssSet(topupKey(), '1'); // checked this session
        if (topup.queued > 0) {
          syncState.phase = 'processing';
          syncState.candidateWatermarkTs = Math.max(syncState.candidateWatermarkTs || 0, topup.newest, Date.now());
          return saveState().then(function () { return true; });
        }
        syncState.watermarkTs = Math.max(syncState.watermarkTs || 0, topup.newest);
        syncState.lastTopUpAt = Date.now();
        return saveState().then(pruneOld).then(function () { return false; });
      });
    });
  }

  function finishBuild() {
    syncState.phase = 'complete';
    syncState.watermarkTs = Math.max(syncState.watermarkTs || 0, syncState.candidateWatermarkTs || 0);
    syncState.lastSyncAt = Date.now();
    ssSet(topupKey(), '1'); // just synced — no separate top-up needed this session
    return saveState().then(pruneOld);
  }

  /* Resolve each line's SKU (top-level for Shopify orders; parsed out of the
     "UPC: … · SKU: …" meta string for ERP invoices) and sum split lines. */
  function aggregateLines(nodes) {
    var bySku = new Map();
    (nodes || []).forEach(function (li) {
      var sku = li.sku;
      if (!sku && li.meta) {
        var m = META_SKU_RE.exec(li.meta);
        if (m) sku = m[1];
      }
      if (!sku) return;
      bySku.set(sku, (bySku.get(sku) || 0) + (li.quantity || 0));
    });
    return bySku;
  }

  /* Latest-purchase-wins merge, atomic per order. Returns the written rows. */
  function mergeOrderLines(orderId, orderTs, bySku) {
    if (!bySku.size) return Promise.resolve([]);
    return openDb().then(function (db) {
      return new Promise(function (resolve, reject) {
        var t = db.transaction('skus', 'readwrite');
        var store = t.objectStore('skus');
        var changed = [];
        bySku.forEach(function (qty, sku) {
          var getReq = store.get(sku);
          getReq.onsuccess = function () {
            var cur = getReq.result;
            var next = null;
            if (!cur || orderTs > cur.ts || (orderTs === cur.ts && cur.orderId === orderId)) {
              next = { sku: sku, ts: orderTs, qty: qty, orderId: orderId };
            } else if (orderTs === cur.ts) {
              next = { sku: sku, ts: orderTs, qty: cur.qty + qty, orderId: cur.orderId };
            }
            if (next) { store.put(next); changed.push(next); }
          };
        });
        t.oncomplete = function () { resolve(changed); };
        t.onerror = function () { reject(t.error); };
        t.onabort = function () { reject(t.error); };
      });
    });
  }

  /* Drop entries older than the rolling window (render filters too — this
     just keeps the store from growing stale). */
  function pruneOld() {
    var cutoff = Date.now() - months * MONTH_MS;
    return openDb().then(function (db) {
      return new Promise(function (resolve) {
        var t = db.transaction('skus', 'readwrite');
        var req = t.objectStore('skus').index('ts').openCursor(window.IDBKeyRange.upperBound(cutoff));
        req.onsuccess = function () {
          var cur = req.result;
          if (!cur) return;
          lastOrdered.delete(cur.value.sku);
          cur.delete();
          cur.continue();
        };
        t.oncomplete = function () { resolve(); };
        t.onerror = function () { resolve(); };
      });
    }).catch(function () {});
  }

  /* ── lifecycle: logout purge, guest sweep, identity check ──────────── */

  function purgeOnLogout() {
    stopped = true;
    endLoop();
    ssRemove(topupKey());
    // Best-effort before navigation; the marker survives an interrupted
    // delete, so the guest sweep finishes the job on the next page load.
    deleteDatabase(function () { lsRemove(OWNER_KEY); });
  }

  document.addEventListener('click', function (e) {
    var anchor = null;
    var path = e.composedPath ? e.composedPath() : [];
    for (var i = 0; i < path.length; i++) {
      var n = path[i];
      if (n && n.tagName === 'A' && typeof n.href === 'string' && n.href.indexOf('/account/logout') !== -1) {
        anchor = n;
        break;
      }
    }
    if (!anchor && e.target && e.target.closest) anchor = e.target.closest('a[href*="/account/logout"]');
    if (anchor) purgeOnLogout();
  }, true);

  function sweepStaleDb() {
    if (!lsGet(OWNER_KEY)) return;
    deleteDatabase(function () { lsRemove(OWNER_KEY); });
  }

  /* ── boot ──────────────────────────────────────────────────────────── */

  function bootLegacy() {
    // Non-B2B: no proxy auth — reduce the Liquid dump in memory (v1 behavior).
    sweepStaleDb(); // any DB on disk belongs to a (different) B2B login
    var lines = window.VALOR_ORDER_LINES || [];
    lines.forEach(function (l) {
      if (!l || !l.sku) return;
      var ts = (l.ts || 0) * 1000;
      var cur = lastOrdered.get(l.sku);
      if (!cur || ts > cur.ts) {
        lastOrdered.set(l.sku, { ts: ts, qty: l.qty, date: l.date });
      } else if (ts === cur.ts) {
        cur.qty += l.qty;
      }
    });
    startRenderer();
  }

  function bootB2B() {
    idbGet('meta', 'state').then(function (state) {
      if (!state || String(state.customerId) !== String(config.customerId)) {
        // First build, or another customer's data on this browser: start clean.
        return Promise.all([idbClear('skus'), idbClear('queue'), idbClear('meta')]).then(function () {
          syncState = freshState();
          return saveState();
        });
      }
      syncState = state;
    }).then(function () {
      lsSet(OWNER_KEY, String(config.customerId));
      return idbGetAll('skus');
    }).then(function (entries) {
      (entries || []).forEach(function (e) { lastOrdered.set(e.sku, { ts: e.ts, qty: e.qty }); });
      startRenderer();
      // No sync in the theme editor, and none without proxy auth (renderer
      // still serves whatever the DB already holds).
      if (!config.designMode && window.__vaAuth) {
        maybeStartSync();
        document.addEventListener('visibilitychange', function () {
          if (!document.hidden) maybeStartSync();
        });
      }
    }).catch(function (err) {
      // IndexedDB unavailable (private mode etc.) — accepted empty state.
      if (window.console && console.warn) console.warn('[vph] disabled:', err && err.message);
    });
  }

  if (!config || !window.__vrIsCustomer) {
    sweepStaleDb();
  } else if (config.b2b !== true) {
    bootLegacy(); // mirrors the snippet's `unless b2b?` dump gate
  } else {
    bootB2B();
  }

  /* Console access for QA (verification checklist). */
  window.__vphDebug = {
    get state() { return syncState; },
    map: lastOrdered,
    scan: scan
  };
})();
